/**
 * รูปข้อมูลของรายงาน "รายการชำระเงิน" (GET /report/payment-list)
 *
 * 1 แถว = 1 ประเภทค่าบริการ — ใบแจ้งหนี้ที่มีทั้งค่าเช่า/ค่าน้ำ/ค่าไฟ จะได้ 3 แถว
 * (เลขที่ใบเดียวกัน `description` ต่างกัน) จึงมี `isFirstRowOfInvoice` มาให้แยกแถวแรกของใบ
 */
export interface PaymentListRow {
  /** ต่อเนื่องข้ามหน้ามาจาก backend แล้ว — ห้ามบวก offset ของหน้าเพิ่ม */
  seq: number;
  invoiceId: number;
  invoiceNo: string | null;
  /** yyyy-MM-dd ไว้ sort/เทียบค่า — ที่เอาไปแสดงคือ *Text ที่เป็น พ.ศ. แล้ว */
  invoiceDate: string | null;
  invoiceDateText: string | null;
  dateTime:string | null;
  periodDate: string | null;
  periodDateText: string | null;
  dueDate: string | null;
  dueDateText: string | null;
  customerName: string | null;
  /** null ได้ — ข้อมูลเก่าบางใบไม่มีโซน/แผง/ประเภทบริการ */
  zoneId: number | null;
  zoneName: string | null;
  stallId: number | null;
  stallName: string | null;
  stallNumber: string | null;
  paymentServiceId: number | null;
  description: string | null;
  amount: number | null;
  /** ยอดของ "ทั้งใบ" ไม่ใช่ของแถว — ซ้ำเหมือนกันทุกแถวของใบเดียวกัน */
  fineAmount: number | null;
  totalAmount: number | null;
  /** true = แถวแรกของใบนี้ ใช้คุมว่าจะโชว์ fineAmount/totalAmount หรือเว้นว่าง */
  isFirstRowOfInvoice: boolean;
  status?: string | null;
}

/**
 * ยอดรวมของ "ผลลัพธ์ทั้งชุดที่ผ่าน filter" ไม่ใช่เฉพาะหน้าที่เปิดอยู่
 * แถวรวมทั้งสิ้นต้องอ่านจากตัวนี้ ห้ามบวกจาก data เอง (จะได้ยอดแค่หน้าเดียวและนับค่าปรับซ้ำ)
 */
export interface PaymentListSummary {
  /** = total ใช้คุม pagination */
  totalRows: number;
  /** จำนวนใบแจ้งหนี้ — น้อยกว่า totalRows ถ้ามีใบหลายบริการ */
  totalInvoices: number;
  sumAmount: number;
  /** บวกต่อใบ ไม่ใช่ต่อแถว */
  sumFineAmount: number;
  sumTotalAmount: number;
}

export const EMPTY_PAYMENT_LIST_SUMMARY: PaymentListSummary = {
  totalRows: 0,
  totalInvoices: 0,
  sumAmount: 0,
  sumFineAmount: 0,
  sumTotalAmount: 0,
};

/** ตัวเลือก dropdown "ประเภท" — เส้น payment-type คืน field มามากกว่านี้ แต่ใช้แค่ 2 ตัว */
export interface PaymentTypeOption {
  paymentServiceId: number;
  paymentName: string | null;
}

/**
 * โหมดแสดงคอลัมน์ `ค่าปรับ` / `รวมทั้งหมด` ซึ่งเป็นยอดของ "ทั้งใบ" ไม่ใช่ของแถว
 * (ใบที่มี 3 ประเภทบริการ จะได้ 3 แถวที่ถือยอด 2 ตัวนี้ซ้ำกัน)
 *
 * มีไว้เพราะ doc API กับภาพดีไซน์ระบุไม่ตรงกัน จึงให้สลับได้แทนที่จะต้องรื้อ renderRow
 */
export enum InvoiceAmountDisplay {
  /**
   * ตาม doc API — โชว์เฉพาะแถวแรกของใบ แถวถัดไปเว้นว่าง
   * ยอดที่ผู้ใช้บวกจากคอลัมน์เองจะตรงกับแถว "รวมทั้งสิ้น" และตรงกับไฟล์ Excel
   */
  FIRST_ROW_ONLY = "FIRST_ROW_ONLY",
  /**
   * ตามภาพดีไซน์ — พิมพ์ซ้ำทุกแถวของใบเดียวกัน
   * อ่านง่ายขึ้นเวลาไล่ทีละแถว แต่ผู้ใช้ที่บวกคอลัมน์เองจะได้ยอดเกิน
   * (ใบมี 3 บริการ ค่าปรับ 2,000 จะดูเหมือน 6,000) และไม่ตรงกับ `รวมทั้งสิ้น`
   * ที่ backend บวกต่อใบ — แถวรวมกับไฟล์ Excel ไม่เปลี่ยนตามโหมดนี้
   */
  EVERY_ROW = "EVERY_ROW",
}

/**
 * โหมดที่ใช้อยู่ — **สลับค่าบรรทัดนี้จุดเดียว** ไม่ต้องแก้ที่อื่น
 * ค่าเริ่มต้นเป็น FIRST_ROW_ONLY ตามที่สรุปกับผู้ใช้ (ยึด doc API)
 */
export const INVOICE_AMOUNT_DISPLAY: InvoiceAmountDisplay = InvoiceAmountDisplay.EVERY_ROW;
